import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Lock,
  Visibility,
  VisibilityOff,
  Email,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/system.store';
import { AgentService } from '../../services/agent.service';

interface SetPasswordData {
  agentEmail: string;
  newPassword: string;
}

const SetAgentPasswordPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState<SetPasswordData>({
    agentEmail: '',
    newPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<SetPasswordData>>({});
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<SetPasswordData> = {};
    
    if (!formData.agentEmail) {
      newErrors.agentEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.agentEmail)) {
      newErrors.agentEmail = 'Please enter a valid email address';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccess(false);

    try {
      const response = await AgentService.setClientPassword(formData.agentEmail, formData.newPassword);

      if (response.success) {
        setSuccess(true);
        setFormData({ agentEmail: '', newPassword: '' });
        
        dispatch(
          showSnackbar({
            isError: false,
            msg: response.message || 'Agent password updated successfully!',
          })
        );
      } else {
        throw new Error(response.message || 'Failed to update agent password');
      }
    } catch (error) {
      console.error('Error updating agent password:', error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: error instanceof Error ? error.message : 'Failed to update agent password. Please try again.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof SetPasswordData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box 
        sx={{ 
          padding: { xs: "16px", md: "24px" }, 
          backgroundColor: theme.palette.grey[50], 
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton 
            onClick={() => navigate('/admin')} 
            sx={{ 
              p: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: theme.palette.grey[100] }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            Set Agent Password
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 600 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Update Agent Password
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Agent password updated successfully! The agent can now log in with the new password.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Agent Email Address"
                  type="email"
                  value={formData.agentEmail}
                  onChange={handleInputChange('agentEmail')}
                  error={!!errors.agentEmail}
                  helperText={errors.agentEmail}
                  required
                  InputProps={{
                    startAdornment: (
                      <Email color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={handleInputChange('newPassword')}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  required
                  InputProps={{
                    startAdornment: (
                      <Lock color="action" sx={{ mr: 1 }} />
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading || !formData.agentEmail || !formData.newPassword}
                  startIcon={<Lock />}
                  sx={{ textTransform: 'none' }}
                >
                  {isLoading ? 'Updating Password...' : 'Update Agent Password'}
                </Button>
              </Stack>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default SetAgentPasswordPage;